/**
 * 固定音声（事前生成したファイル）を鳴らすだけの再生器。
 *
 * `<audio>` 要素を使わない理由：iOS ではマナーモード時に `<audio>` が鳴らない。
 * 「聞くだけ」で使う人はまさに音を出しにくい場所にいるので、そこで無音になると
 * この入口の存在理由が消える。AudioContext なら鳴ることは実機で確認済み。
 *
 * 将来アプリ化するときに差し替えられるよう、会話本体（ConversationEngine）とは
 * 独立させ、再生インターフェースだけを外に出している。
 */
export class ClipPlayer {
  private ctx: AudioContext | null = null;
  private cache = new Map<string, AudioBuffer>();
  private current: AudioBufferSourceNode | null = null;

  /**
   * 必ずユーザー操作から呼ぶこと。iOS の AudioContext は suspended で生成される。
   */
  async unlock(): Promise<void> {
    if (this.ctx) {
      if (this.ctx.state === "suspended") await this.ctx.resume();
      return;
    }
    const Ctor =
      window.AudioContext ??
      (window as unknown as { webkitAudioContext: typeof AudioContext })
        .webkitAudioContext;
    this.ctx = new Ctor();
    await this.ctx.resume();

    // iOS の初回再生は 546ms かかる。無音を 1 回流して温めておく。
    const buf = this.ctx.createBuffer(1, 1, this.ctx.sampleRate);
    const src = this.ctx.createBufferSource();
    src.buffer = buf;
    src.connect(this.ctx.destination);
    src.start();
  }

  /**
   * 鳴らし終わるまで待つ。ファイルが無い・デコードできない場合は false を返して
   * 即座に解決する。音声を用意する前でも画面の流れが止まらないようにするため。
   */
  async play(src: string): Promise<boolean> {
    await this.unlock();
    const ctx = this.ctx;
    if (!ctx) return false;

    const buffer = await this.load(src, ctx);
    if (!buffer) return false;

    this.stop();
    return new Promise<boolean>((resolve) => {
      const node = ctx.createBufferSource();
      node.buffer = buffer;
      node.connect(ctx.destination);
      node.onended = () => {
        if (this.current === node) this.current = null;
        resolve(true);
      };
      this.current = node;
      node.start();
    });
  }

  stop(): void {
    if (!this.current) return;
    this.current.onended = null;
    try {
      this.current.stop();
    } catch {
      // すでに停止済み。無視してよい。
    }
    this.current = null;
  }

  dispose(): void {
    this.stop();
    this.cache.clear();
    void this.ctx?.close();
    this.ctx = null;
  }

  private async load(
    src: string,
    ctx: AudioContext
  ): Promise<AudioBuffer | null> {
    const cached = this.cache.get(src);
    if (cached) return cached;

    try {
      const res = await fetch(src);
      if (!res.ok) return null;
      const bytes = await res.arrayBuffer();

      // 古い Safari は Promise を返さず、コールバック形式のみを受ける。
      const buffer = await new Promise<AudioBuffer>((resolve, reject) => {
        const maybe = ctx.decodeAudioData(bytes, resolve, reject);
        if (maybe && typeof maybe.then === "function") {
          maybe.then(resolve, reject);
        }
      });

      this.cache.set(src, buffer);
      return buffer;
    } catch {
      return null;
    }
  }
}
